import { type Decimal, dec, ZERO } from "@bl/domain";
import { clearLocal, readLocal, writeLocal } from "./autosave";
import type { QuantityUnit } from "./components/ui";

/** A row's count as typed: its amount and unit, and what the sheet showed when the count began. */
export interface Counted {
  value: string;
  unit: QuantityUnit;
  /** Canonical: on record here when this row was first typed in. The count is taken against it. */
  expected: string;
}

/**
 * What this device keeps of a count sheet not yet recorded (the re-critique, 2026-10-07: the sheet lost
 * every count typed the moment it was left). The counts, and the idempotency key of any row already
 * sent, so a row whose reply never came is sent again under the same key and is not counted twice.
 * Kept on this device only: a count is taken by whoever is standing in the walk-in, on what they hold.
 */
export interface CountDraft {
  counts: Record<string, Counted>;
  keys: Record<string, string>;
}

const draftKey = (locationId: string) => `bl.count.${locationId}`;
const LAST_KEY = "bl.count.last";

export function readCountDraft(locationId: string) {
  return readLocal<CountDraft>(draftKey(locationId));
}

/**
 * Keeps the sheet, or forgets it once nothing on it is waiting: a row left empty is not a count.
 * `begun` is when its first row was typed.
 */
export function writeCountDraft(locationId: string, draft: CountDraft, begun: string) {
  const counts = Object.fromEntries(
    Object.entries(draft.counts).filter(([, c]) => c.value.trim() !== ""),
  );
  if (Object.keys(counts).length === 0 && Object.keys(draft.keys).length === 0) {
    clearLocal(draftKey(locationId));
  } else {
    writeLocal(draftKey(locationId), { value: { counts, keys: draft.keys }, savedAt: begun });
  }
}

/** How many counts wait unrecorded on this device for a location. */
export const unrecordedCounts = (locationId: string) =>
  Object.keys(readCountDraft(locationId)?.value.counts ?? {}).length;

/** The location last counted on this device, where the sheet opens next. */
export function lastCounted(): string | null {
  return readLocal<string>(LAST_KEY)?.value ?? null;
}

export function rememberCounted(locationId: string) {
  writeLocal(LAST_KEY, { value: locationId, savedAt: new Date().toISOString() });
}

/**
 * Whether a count is far enough from the record to be worth a second look before it is recorded, and
 * in what words: twice the record or more, half or less, or none at all. A slip of the unit (pounds
 * typed where bags were meant) or of the decimal point lands here. Nothing on record is no yardstick.
 */
export function farFromRecord(record: Decimal, counted: Decimal): string | null {
  if (record.lte(ZERO)) return null;
  if (counted.eq(ZERO)) return "none left";
  if (counted.gte(record.times(dec("2")))) {
    return `${counted.div(record).round(0, 0).toString()} times what is on record`;
  }
  if (counted.lte(record.div(dec("2")))) return "half or less of what is on record";
  return null;
}
